import { HttpBackend, HttpErrorResponse, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { DestroyRef, Injectable, Signal, inject, signal } from '@angular/core';
import { Observable, catchError, filter, firstValueFrom, map, of, timeout } from 'rxjs';
import { MessagePopupService } from '../../shared/components/message-popup/message-popup.service';
import { EnvironmentService } from './environment.service';
import { UtilsService } from './utils.service';

export type ServiceOutageReason = 'unknown' | 'maintenance' | 'offline' | 'unreachable';

// Open, session-free route (rezervari RoutesLoader), so the probe never needs auth and cannot be
// counted as a security violation by the Content-Hash guard. It answers with the maintenance 503
// whenever the API is down.
const PROBE_PATH = '/version';
const PROBE_TIMEOUT_MS = 5_000;
const RETRY_DELAYS_MS = [10_000, 30_000, 60_000, 120_000, 300_000];
const RECOVERED_VISIBLE_MS = 5_000;
const RETRY_JITTER_RATIO = 0.2;
const OUTAGE_POPUP_TEXT =
	'Serviciul este temporar indisponibil. Acțiunea nu a fost finalizată. Vă rugăm să încercați din nou în câteva minute.';

// Shared across the singleton service so a second outage cannot reopen the
// dialog while the first one is still on screen.
let serviceStatusPopup: Promise<unknown> | null = null;

@Injectable({
	providedIn: 'root',
})
export class ServiceStatusService {
	private readonly httpBackend = inject(HttpBackend);
	private readonly envService = inject(EnvironmentService);
	private readonly utilsService = inject(UtilsService);
	private readonly messagePopup = inject(MessagePopupService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly apiUrl: string = this.envService.getValue('apiUrl');

	private readonly outageState = signal(false);
	private readonly reasonState = signal<ServiceOutageReason>('unknown');
	private readonly checkingState = signal(false);
	private readonly recoveredState = signal(false);

	readonly outage: Signal<boolean> = this.outageState.asReadonly();
	readonly reason: Signal<ServiceOutageReason> = this.reasonState.asReadonly();
	readonly checking: Signal<boolean> = this.checkingState.asReadonly();
	readonly recovered: Signal<boolean> = this.recoveredState.asReadonly();

	private probeInFlight: Promise<void> | null = null;
	private retryTimer: ReturnType<typeof setTimeout> | null = null;
	private recoveredTimer: ReturnType<typeof setTimeout> | null = null;
	private retryIndex = 0;
	private destroyed = false;

	constructor() {
		document.addEventListener('visibilitychange', this.onVisibilityChange);
		window.addEventListener('online', this.onOnline);
		this.destroyRef.onDestroy(() => {
			this.destroyed = true;
			serviceStatusPopup = null;
			document.removeEventListener('visibilitychange', this.onVisibilityChange);
			window.removeEventListener('online', this.onOnline);
			this.clearTimers();
		});
	}

	reportFailure(error: unknown): void {
		if (this.outageState()) {
			return;
		}

		this.clearRecoveredTimer();
		this.recoveredState.set(false);
		this.outageState.set(true);
		this.reasonState.set(this.reasonFromFailure(error));
		this.showDialogOnce();
		this.probe();
	}

	whenDialogClosed(): Promise<unknown> {
		// A rejected dialog promise must not skip the caller's reload.
		return serviceStatusPopup ? serviceStatusPopup.catch(() => undefined) : Promise.resolve();
	}

	private showDialogOnce(): void {
		if (serviceStatusPopup) {
			return;
		}

		let dialog: Promise<unknown>;
		try {
			dialog = this.messagePopup.showMessage(
				OUTAGE_POPUP_TEXT,
				'warning',
				'',
				'Am înțeles',
				{ disableClose: false },
			);
		} catch {
			// A synchronous dialog failure must not abort reportFailure before its probe starts.
			return;
		}

		const opening = Promise.resolve(dialog)
			.catch(() => undefined)
			.finally(() => {
				if (serviceStatusPopup === opening) {
					serviceStatusPopup = null;
				}
			});
		serviceStatusPopup = opening;
	}

	retryNow(): void {
		if (!this.outageState()) {
			return;
		}

		this.clearRetryTimer();
		this.probe();
	}

	private readonly onVisibilityChange = (): void => {
		if (document.hidden || !this.outageState()) {
			return;
		}

		this.clearRetryTimer();
		this.probe();
	};

	private readonly onOnline = (): void => {
		if (!this.outageState()) {
			return;
		}

		this.clearRetryTimer();
		this.probe();
	};

	private probe(): void {
		if (document.hidden || this.probeInFlight) {
			return;
		}

		this.checkingState.set(true);
		// Probe state is already reflected in the signals; never leave the
		// promise rejected or `checking` stuck if a continuation throws.
		this.probeInFlight = this.runProbe()
			.catch(() => undefined)
			.finally(() => {
				this.checkingState.set(false);
				this.probeInFlight = null;
			});
	}

	private async runProbe(): Promise<void> {
		const outcome = await firstValueFrom(this.probeOnce());
		if (this.destroyed) {
			return;
		}

		if (outcome === 'ok') {
			this.recover();
			return;
		}

		this.reasonState.set(outcome);
		this.scheduleRetry();
	}

	private probeOnce(): Observable<'ok' | ServiceOutageReason> {
		// The probe is a preflighted request by design: the headers below are the same ones the
		// content-hash interceptor sends, so the maintenance OPTIONS exemption is what lets the
		// browser read the edge 503 instead of failing at preflight.
		const request = new HttpRequest('GET', `${this.apiUrl}${PROBE_PATH}`, {
			headers: new HttpHeaders({
				'Content-Hash': this.envService.getValue('contentHash'),
				'X-Date': this.utilsService.dateToYmd(new Date()),
			}),
		});

		return this.httpBackend.handle(request).pipe(
			timeout(PROBE_TIMEOUT_MS),
			// Raw HttpBackend emits Sent before the response; without this filter the probe would
			// report success and abort the XHR as soon as the request starts.
			filter((event): event is HttpResponse<unknown> => event instanceof HttpResponse),
			map(() => 'ok' as const),
			catchError((error: unknown) => of(this.classifyProbeError(error))),
		);
	}

	private recover(): void {
		this.clearTimers();
		this.retryIndex = 0;
		this.outageState.set(false);
		this.reasonState.set('unknown');
		this.recoveredState.set(true);
		this.recoveredTimer = setTimeout(() => {
			this.recoveredTimer = null;
			this.recoveredState.set(false);
		}, RECOVERED_VISIBLE_MS);
	}

	private scheduleRetry(): void {
		this.clearRetryTimer();

		const baseDelay = RETRY_DELAYS_MS[this.retryIndex];
		const jitter = 1 - RETRY_JITTER_RATIO + Math.random() * RETRY_JITTER_RATIO * 2;
		const delay = Math.round(baseDelay * jitter);
		this.retryIndex = Math.min(this.retryIndex + 1, RETRY_DELAYS_MS.length - 1);

		this.retryTimer = setTimeout(() => {
			this.retryTimer = null;
			this.probe();
		}, delay);
	}

	private reasonFromFailure(error: unknown): ServiceOutageReason {
		if (!(error instanceof HttpErrorResponse)) {
			return 'unknown';
		}

		return error.status === 503 ? 'maintenance' : this.reasonFromConnectivity();
	}

	private classifyProbeError(error: unknown): 'ok' | ServiceOutageReason {
		if (error instanceof HttpErrorResponse) {
			if (error.status === 503) {
				return 'maintenance';
			}
			// Any other status means the server answered, so it is reachable.
			if (error.status > 0) {
				return 'ok';
			}
		}

		return this.reasonFromConnectivity();
	}

	private reasonFromConnectivity(): ServiceOutageReason {
		return navigator.onLine === false ? 'offline' : 'unreachable';
	}

	private clearRetryTimer(): void {
		if (this.retryTimer !== null) {
			clearTimeout(this.retryTimer);
			this.retryTimer = null;
		}
	}

	private clearRecoveredTimer(): void {
		if (this.recoveredTimer !== null) {
			clearTimeout(this.recoveredTimer);
			this.recoveredTimer = null;
		}
	}

	private clearTimers(): void {
		this.clearRetryTimer();
		this.clearRecoveredTimer();
	}
}
